import React, { useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Divider,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

const MedicalHistoryTab = ({ data, onChange }) => {
  const [newDiagnosis, setNewDiagnosis] = useState("");
  const [newSurgery, setNewSurgery] = useState({ procedure: "", date: "" });
  const [newFamilyHistory, setNewFamilyHistory] = useState({
    condition: "",
    relation: "",
  });

  // Common diagnoses checklist
  const commonDiagnoses = [
    "Hypertension",
    "Diabetes Type 2",
    "Diabetes Type 1",
    "Heart Disease",
    "Kidney Disease",
    "Liver Disease",
    "Cancer",
    "Asthma",
    "COPD",
    "Arthritis",
    "Depression",
    "Anxiety",
  ];

  const handleCheckboxDiagnosis = (diagnosis, checked) => {
    const updatedList = checked
      ? [...(data.pastDiagnoses || []), diagnosis]
      : (data.pastDiagnoses || []).filter((d) => d !== diagnosis);

    onChange({
      ...data,
      pastDiagnoses: updatedList,
    });
  };

  const handleAddCustomDiagnosis = () => {
    if (newDiagnosis.trim()) {
      onChange({
        ...data,
        pastDiagnoses: [...(data.pastDiagnoses || []), newDiagnosis.trim()],
      });
      setNewDiagnosis("");
    }
  };

  const handleRemoveDiagnosis = (diagnosis) => {
    onChange({
      ...data,
      pastDiagnoses: (data.pastDiagnoses || []).filter((d) => d !== diagnosis),
    });
  };

  const handleAddSurgery = () => {
    if (newSurgery.procedure.trim()) {
      onChange({
        ...data,
        pastSurgeries: [...(data.pastSurgeries || []), newSurgery],
      });
      setNewSurgery({ procedure: "", date: "" });
    }
  };

  const handleRemoveSurgery = (index) => {
    onChange({
      ...data,
      pastSurgeries: (data.pastSurgeries || []).filter((_, i) => i !== index),
    });
  };

  const handleAddFamilyHistory = () => {
    if (newFamilyHistory.condition.trim()) {
      onChange({
        ...data,
        familyHistory: [...(data.familyHistory || []), newFamilyHistory],
      });
      setNewFamilyHistory({ condition: "", relation: "" });
    }
  };

  const handleRemoveFamilyHistory = (index) => {
    onChange({
      ...data,
      familyHistory: (data.familyHistory || []).filter((_, i) => i !== index),
    });
  };

  return (
    <Box>
      {/* Past Diagnoses */}
      <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
        Past Diagnoses
      </Typography>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Select all that apply:
        </Typography>
        <FormGroup>
          <Grid container spacing={1}>
            {commonDiagnoses.map((diagnosis) => (
              <Grid item xs={12} sm={6} md={4} key={diagnosis}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={(data.pastDiagnoses || []).includes(diagnosis)}
                      onChange={(e) =>
                        handleCheckboxDiagnosis(diagnosis, e.target.checked)
                      }
                    />
                  }
                  label={diagnosis}
                />
              </Grid>
            ))}
          </Grid>
        </FormGroup>
      </Box>

      {/* Add custom diagnosis */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={10}>
          <TextField
            fullWidth
            label="Add Other Diagnosis"
            placeholder="Enter diagnosis not listed above"
            value={newDiagnosis}
            onChange={(e) => setNewDiagnosis(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAddCustomDiagnosis();
              }
            }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddCustomDiagnosis}
            sx={{ height: "56px" }}
          >
            Add
          </Button>
        </Grid>
      </Grid>

      {/* Display selected diagnoses */}
      {data.pastDiagnoses && data.pastDiagnoses.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Selected Diagnoses:
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {data.pastDiagnoses.map((diagnosis, index) => (
              <Chip
                key={index}
                label={diagnosis}
                onDelete={() => handleRemoveDiagnosis(diagnosis)}
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Past Surgeries */}
      <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
        Past Surgeries
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Procedure/Surgery"
            value={newSurgery.procedure}
            onChange={(e) =>
              setNewSurgery({ ...newSurgery, procedure: e.target.value })
            }
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={newSurgery.date}
            onChange={(e) =>
              setNewSurgery({ ...newSurgery, date: e.target.value })
            }
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddSurgery}
            sx={{ height: "56px" }}
          >
            Add
          </Button>
        </Grid>
      </Grid>

      {/* Display surgeries */}
      {data.pastSurgeries && data.pastSurgeries.length > 0 && (
        <Box sx={{ mb: 4 }}>
          {data.pastSurgeries.map((surgery, index) => (
            <Card key={index} sx={{ mb: 2 }}>
              <CardContent
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    {surgery.procedure}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {surgery.date
                      ? new Date(surgery.date).toLocaleDateString()
                      : "Date not specified"}
                  </Typography>
                </Box>
                <IconButton
                  color="error"
                  onClick={() => handleRemoveSurgery(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <Divider sx={{ my: 3 }} />

      {/* Family History */}
      <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
        Family Medical History
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={5}>
          <TextField
            fullWidth
            label="Medical Condition"
            value={newFamilyHistory.condition}
            onChange={(e) =>
              setNewFamilyHistory({
                ...newFamilyHistory,
                condition: e.target.value,
              })
            }
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <TextField
            fullWidth
            label="Relation"
            placeholder="e.g., Mother, Father, Sibling"
            value={newFamilyHistory.relation}
            onChange={(e) =>
              setNewFamilyHistory({
                ...newFamilyHistory,
                relation: e.target.value,
              })
            }
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddFamilyHistory}
            sx={{ height: "56px" }}
          >
            Add
          </Button>
        </Grid>
      </Grid>

      {/* Display family history */}
      {data.familyHistory && data.familyHistory.length > 0 && (
        <Box>
          {data.familyHistory.map((history, index) => (
            <Card key={index} sx={{ mb: 2 }}>
              <CardContent
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    {history.condition}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Relation: {history.relation}
                  </Typography>
                </Box>
                <IconButton
                  color="error"
                  onClick={() => handleRemoveFamilyHistory(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default MedicalHistoryTab;
